import React, { useState } from 'react';
import { FadeIn } from './FadeIn';
import { ChevronDown, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const faqs = [{
  question: 'Why not just hire a medical billing advocate?',
  answer: 'You can. Most advocates charge 20–30% of whatever they save you.\n\nThis blueprint gives you the same structured negotiation process — without giving away a percentage.\n\nYou keep the savings. And you keep the skill permanently.'
}, {
  question: 'Can I really negotiate a medical bill?',
  answer: "Yes. Hospitals expect negotiation. Insurance companies expect appeals. Most patients simply don't know how to structure the conversation.\n\nThis guide shows you how to do it correctly."
}, {
  question: 'What if insurance already processed the claim?',
  answer: 'You can still question charges, request itemization, and appeal decisions. Processing does not mean final.'
}, {
  question: 'Will negotiating hurt my credit?',
  answer: 'Not if handled before collections. This guide shows you how to communicate strategically and document everything properly.'
}, {
  question: "What if I'm not good at confrontation?",
  answer: "You don't need confrontation. You need structure.\n\nThe scripts are designed to be calm, clear, and professional — not aggressive."
}, {
  question: 'How long does this take?',
  answer: 'Most steps can be done in short sessions. The 30-Day Action Plan breaks it down so it feels manageable.'
}];
const forYou = ['You manage your household budget', "You're middle-income — too much for aid, not enough for costly mistakes", "You've ever felt blindsided by a medical bill", 'You want clarity and control, not conflict'];
const notForYou = ['You assume every bill is automatically correct', "You're unwilling to make a phone call", 'You prefer to ignore problems and hope they disappear'];
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="py-12 md:py-16 bg-cream-50" data-id="element-23">
      <div className="max-w-3xl mx-auto px-6" data-id="element-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14" data-id="element-25">
          <FadeIn delay={0.1} data-id="element-26">
            <div className="bg-white rounded-xl border border-cream-200 p-6" data-id="element-27">
              <h3 className="font-bold text-brown-900 text-lg mb-4" data-id="element-28">
                This Is For You If:
              </h3>
              <ul className="space-y-3" data-id="element-29">
                {forYou.map((item, i) => <li key={i} className="flex items-start gap-2.5" data-id="element-30">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" data-id="element-31" />
                    <span className="text-brown-700 text-sm leading-relaxed" data-id="element-32">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-33">
            <div className="bg-white rounded-xl border border-cream-200 p-6" data-id="element-34">
              <h3 className="font-bold text-brown-900 text-lg mb-4" data-id="element-35">
                This Is NOT For You If:
              </h3>
              <ul className="space-y-3" data-id="element-36">
                {notForYou.map((item, i) => <li key={i} className="flex items-start gap-2.5" data-id="element-37">
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" data-id="element-38" />
                    <span className="text-brown-700 text-sm leading-relaxed" data-id="element-39">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>

        <FadeIn data-id="element-40">
          <h2 className="text-2xl md:text-4xl text-brown-900 text-center mb-10" data-id="element-41">
            Frequently Asked Questions
          </h2>
        </FadeIn>

        <div className="space-y-3" data-id="element-42">
          {faqs.map((faq, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-43">
              <div className="bg-white rounded-xl border border-cream-200 overflow-hidden" data-id="element-44">
                <button onClick={() => toggle(i)} className="w-full flex items-center justify-between p-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 focus-visible:ring-offset-2 rounded-xl" aria-expanded={openIndex === i} data-id="element-45">
                  <span className="font-semibold text-brown-900 text-sm md:text-base pr-4" data-id="element-46">
                    {faq.question}
                  </span>
                  <motion.span animate={{
                rotate: openIndex === i ? 180 : 0
              }} transition={{
                duration: 0.25,
                ease: 'easeInOut'
              }} className="flex-shrink-0" data-id="element-47">
                    <ChevronDown className="w-5 h-5 text-brown-500" data-id="element-48" />
                  </motion.span>
                </button>
                <AnimatePresence initial={false} data-id="element-49">
                  {openIndex === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-50">
                      <div className="px-5 pb-5 pt-0" data-id="element-51">
                        {faq.answer.split('\n\n').map((paragraph, pi) => <p key={pi} className="text-brown-600 text-sm leading-relaxed mb-2 last:mb-0" data-id="element-52">
                            {paragraph}
                          </p>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </FadeIn>)}
        </div>

        <FadeIn delay={0.1} data-id="element-53">
          <div className="text-center mt-10" data-id="element-54">
            <button onClick={() => {
            const el = document.getElementById('final-cta');
            if (el) el.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }} className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white bg-terracotta-500 rounded-full shadow-lg hover:bg-terracotta-600 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-55">
              Get My Blueprint
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-56" />
            </button>
          </div>
        </FadeIn>
      </div>
    </section>;
}